const CARD_BATCH_SIZE = 12 // Max 20, all_products limit

/**
 * Card images appear left to right, top to bottom. A loaded image waits for
 * the cards before it for as long as it took to download itself (so the wait
 * scales with the connection), and at least this long. After that, any
 * earlier image still loading is skipped (it appears when ready) so one slow
 * image can't hold up the rest of the grid.
 */
const IMAGE_REVEAL_MIN_WAIT = 400

/**
 * Card images download a few at a time, in grid order, instead of all at
 * once. On a slow connection that gets each row on screen in turn, rather
 * than every image sharing the bandwidth and finishing together in any order.
 */
const IMAGE_LOAD_CONCURRENCY = 3

const HITS_SKELETON_HTML = `
  <div class="ais-Hits">
    <ol class="ais-Hits-list">
      ${'<li class="ais-Hits-item"><div class="ais-product"><div class="prd-Card-placeholder"></div></div></li>'.repeat(12)}
    </ol>
  </div>
`

class AlgoliaInstantSearchWidget extends window.BAO.CustomElement() {
  connectedCallback () {
    super.connectedCallback()

    this.setupState()
  }

  setupListeners () {
    super.setupListeners()

    document.addEventListener(
      'algolia.hooks.initialize',
      () => {
        this.showHitsSkeleton()
        this.registerWidgetArray()
        this.registerSearchFilters()
        this.registerMainLayout()
        this.registerStatsTemplate()
        this.registerProductTemplate()
        this.registerRefinementOptions()
        this.registerSortOrders()
      },
      { once: true },
    )
  }

  setupState () {
    this.hitsPerPage = 48

    this.productCardCache = new Map()

    this.placeholders = []
    this.placeholderHandles = []
    this.batches = []
    this.batchIndexes = new Map()
    this.renderQueue = Promise.resolve()
    this.imageReveals = new WeakMap()

    this.imageQueue = []
    this.imagesInFlight = new Map()
    this.imageStartTimes = new WeakMap()
    this.nearbyMedia = new WeakSet()

    this.cardObserver = new IntersectionObserver(
      (entries) => this.handleCardIntersections(entries),
      { rootMargin: '100% 0px 100% 0px' },
    )

    /* Card images only start downloading once they're near the viewport */
    this.imageObserver = new IntersectionObserver(
      (entries) => this.handleImageIntersections(entries),
      { rootMargin: '100% 0px 100% 0px' },
    )

    /* Switching card style changes which image is on show */
    this.cardStyleObserver = new MutationObserver(() =>
      this.handleCardStyleChange(),
    )

    this.cardStyleObserver.observe(this, {
      attributes: true,
      attributeFilter: ['data-card-style'],
    })
  }

  registerWidgetArray () {
    /* eslint-disable-next-line no-undef */
    algoliaShopify.hooks.registerHook(
      'beforeInstantSearchStartAddWidgets',
      () => {
        const { configure } = window.algoliaShopify.externals.widgets

        const configureWidget = configure({
          facetingAfterDistinct: false,
        })

        return [configureWidget]
      },
    )
  }

  registerSearchFilters () {
    /* eslint-disable-next-line no-undef */
    algoliaShopify.hooks.registerHook(
      'beforeInstantSearchFiltersString',
      (defaultFilter) => {
        if (defaultFilter.includes('collection_ids') || !this.collectionId) {
          return defaultFilter
        }

        return `collection_ids:"${this.collectionId}" AND ${defaultFilter}`
      },
    )
  }

  registerMainLayout () {
    /* eslint-disable-next-line no-undef */
    algoliaShopify.hooks.registerHook(
      'beforeInstantSearchMainTemplate',
      (_defaultTemplate, data, html) => {
        setTimeout(() => {
          const cardSwitcherHtml = `
            <card-switcher class="ais-CardSwitcher">
              <button
                class="ais-CardSwitcher_Button fz-12_20 fz-12_20-title"
                data-card-switcher-el="trigger"
                data-attribute="card"
                data-style="outfit"
                aria-pressed="${this.dataset.cardStyle === 'outfit'}"
              >
                ${window.theme.strings.algolia.outfit}
              </button>

              <button
                class="ais-CardSwitcher_Button fz-12_20 fz-12_20-title"
                data-card-switcher-el="trigger"
                data-attribute="card"
                data-style="product"
                aria-pressed="${this.dataset.cardStyle === 'product'}"
              >
                ${window.theme.strings.algolia.product}
              </button>
            </card-switcher>
          `

          const gridSwitcherHtml = `
            <card-switcher class="ais-GridSwitcher">
              <button
                class="ais-GridSwitcher_Button"
                data-card-switcher-el="trigger"
                data-attribute="grid"
                data-style="regular"
                aria-pressed="${this.dataset.gridStyle === 'regular'}"
              >
                ${window.theme.strings.algolia.regular}
              </button>

              <button
                class="ais-GridSwitcher_Button"
                data-card-switcher-el="trigger"
                data-attribute="grid"
                data-style="large"
                aria-pressed="${this.dataset.gridStyle === 'large'}"
              >
                ${window.theme.strings.algolia.large}
              </button>
            </card-switcher>
          `

          const displayEl = document.querySelector('.ais-change-display')
          displayEl.insertAdjacentHTML('afterbegin', cardSwitcherHtml)
          displayEl.insertAdjacentHTML('beforeend', gridSwitcherHtml)
        }, 100)

        return _defaultTemplate
      },
    )
  }

  registerStatsTemplate () {
    /* eslint-disable-next-line no-undef */
    algoliaShopify.hooks.registerHook(
      'beforeInstantSearchStatsTemplate',
      (_defaultTemplate, data, html) => {
        this.hitsPerPage = data.hitsPerPage

        const totalCount = data.nbHits || 0

        const fromNumber = data.hitsPerPage * data.page + 1
        const toNumber =
          data.hitsPerPage * (data.page + 1) > totalCount
            ? totalCount
            : data.hitsPerPage * (data.page + 1)

        const label = data.hasOneResult === true ? ' result' : ' results'

        this.updateDocumentTitle(totalCount, data.query)

        /* eslint-disable no-undef */
        return html`
          ${algoliaShopify.helpers.formatNumber(fromNumber)} - ${toNumber} out
          of ${totalCount} ${label}
        `
        /* eslint-enable no-undef */
      },
    )
  }

  updateDocumentTitle (totalCount, terms) {
    if (!terms) return

    const formattedCount = new Intl.NumberFormat().format(totalCount)

    const string =
      totalCount === 1
        ? window.theme.strings.algolia.searchTitle.one
        : window.theme.strings.algolia.searchTitle.other

    const documentTitle = string
      .replaceAll('#count#', formattedCount)
      .replaceAll('#terms#', `"${terms}"`)

    if (documentTitle) {
      document.title = documentTitle
    }
  }

  registerProductTemplate () {
    /* eslint-disable-next-line no-undef */
    algoliaShopify.hooks.registerHook(
      'beforeInstantSearchProductTemplate',
      (defaultTemplate, hit, html, _components, trackSearchAttribution) => {
        if (!hit.handle) {
          return defaultTemplate
        }

        this.queuePlaceholderSync()

        /**
         * Calculate page for collection scroll position, data.page is not
         * accurate in other hooks.
         */
        const page = Math.ceil(hit.productPosition / this.hitsPerPage)

        return html`
          <div
            class="ais-product"
            data-algolia-index="${hit.index}"
            data-algolia-position="${hit.productPosition}"
            data-algolia-queryid="${hit.queryID}"
            data-algolia-objectid="${hit.objectID}"
            data-handle="${hit.handle}"
            data-variant-id="${hit.objectID}"
            data-distinct="${hit._distinct}"
            data-product-id="${hit.id}"
            data-collection-filters-el="item"
            data-page="${page}"
            onClick="${() => trackSearchAttribution(hit)}"
          >
            <div
              class="prd-Card-placeholder"
              data-algolia-product-card-placeholder
              data-card-handle="${hit.handle}"
              data-product-handle
            ></div>
          </div>
        `
      },
    )
  }

  registerRefinementOptions () {
    /* eslint-disable-next-line no-undef */
    algoliaShopify.hooks.registerHook(
      'beforeInstantSearchFacetParamsOptions',
      (params, facet) => {
        // Use a natural sort comparison function instead of 'name:asc'
        params.sortBy = (a, b) => {
          return a.name.localeCompare(b.name, undefined, {
            numeric: true,
            sensitivity: 'base',
          })
        }

        return params
      },
    )
  }

  registerSortOrders () {
    const hooks = [
      'beforeInstantSearchInitCollectionSortOrders',
      'beforeInstantSearchInitSearchSortOrders',
    ]

    hooks.forEach((hook) => {
      /* eslint-disable-next-line no-undef */
      algoliaShopify.hooks.registerHook(hook, (...hookArgs) => {
        const defaultSortOrders = hookArgs.slice(0, -1)

        return this.applySortOrderChanges(defaultSortOrders)
      })
    })
  }

  /**
   * One change per line, where `{primary}` is replaced with the primary
   * products index:
   * - Swap: `{primary}_price_asc => {primary}_market_price_asc`, keeping the
   *   app's label and position (add `| Label` to the end to relabel it)
   * - Add: `{primary}_market_price_asc | Price: Low to High`, added to the end
   */
  applySortOrderChanges (defaultSortOrders) {
    const primaryIndex =
      defaultSortOrders[0]?.value ||
      `${window.algoliaShopify?.config?.index_prefix || ''}products`
    const toIndex = (index = '') =>
      index.trim().replaceAll('{primary}', primaryIndex)

    const sortOrders = defaultSortOrders.map((sortOrder) => ({ ...sortOrder }))
    const lines = (this.dataset.sortOrderChanges || '').split(/\r?\n/)

    lines.forEach((line) => {
      const [indexes, ...labelParts] = line.split('|')
      const label = labelParts.join('|').trim()
      const [from, to] = indexes.includes('=>')
        ? indexes.split('=>').map(toIndex)
        : ['', toIndex(indexes)]

      if (!to || sortOrders.some(({ value }) => value === to)) return

      if (from) {
        const sortOrder = sortOrders.find(({ value }) => value === from)

        if (!sortOrder) return

        sortOrder.value = to
        if (label) sortOrder.label = label
      } else if (label) {
        sortOrders.push({ value: to, label })
      }
    })

    return sortOrders
  }

  showHitsSkeleton () {
    this.hitsSkeletonObserver = new MutationObserver(() => {
      const hitsContainer = this.querySelector('.ais-hits-container')

      if (hitsContainer && !hitsContainer.childElementCount) {
        hitsContainer.innerHTML = HITS_SKELETON_HTML
      }
    })

    this.hitsSkeletonObserver.observe(this, { childList: true, subtree: true })
  }

  queuePlaceholderSync () {
    if (this.syncFrame) return

    this.syncFrame = window.requestAnimationFrame(() => {
      this.syncFrame = null
      this.syncPlaceholders()
    })
  }

  /**
   * Split the rendered hits into fixed batches by result position, so cards
   * are always requested and shown top to bottom.
   */
  syncPlaceholders () {
    this.hitsSkeletonObserver?.disconnect()
    this.classList.remove('ais-Algolia-loading')

    const placeholders = [
      ...this.querySelectorAll(
        '[data-algolia-product-card-placeholder][data-card-handle]',
      ),
    ]

    const handles = placeholders.map(
      (placeholder) => placeholder.dataset.cardHandle,
    )

    const hasChanged =
      placeholders.length !== this.placeholders.length ||
      placeholders.some(
        (placeholder, index) =>
          placeholder !== this.placeholders[index] ||
          handles[index] !== this.placeholderHandles[index],
      )

    if (!hasChanged) return

    this.cardObserver.disconnect()

    this.placeholders = placeholders
    this.placeholderHandles = handles
    this.batches = []
    this.batchIndexes = new Map()

    /* New results don't wait on batches still loading for the old ones */
    this.renderQueue = Promise.resolve()

    /* ...or on their images (this also cancels any still downloading) */
    this.pumpImageQueue()

    for (let start = 0; start < placeholders.length; start += CARD_BATCH_SIZE) {
      const batch = {
        index: this.batches.length,
        placeholders: placeholders.slice(start, start + CARD_BATCH_SIZE),
        handles: [],
        isRequested: false,
      }

      batch.placeholders.forEach((placeholder) => {
        this.batchIndexes.set(placeholder, batch.index)

        if (this.isCardRendered(placeholder)) return

        this.resetPlaceholder(placeholder)
        this.cardObserver.observe(placeholder)
      })

      this.batches.push(batch)
    }

    /**
     * The first batch is always needed straight away. When returning from a
     * product, so is the batch holding the card that was clicked (and every
     * batch above it), so the scroll position can be restored.
     */
    this.requestBatchesThrough(Math.max(0, this.getScrollRestoreBatchIndex()))
  }

  handleCardIntersections (entries) {
    const batchIndexes = entries
      .filter((entry) => entry.isIntersecting)
      .map((entry) => this.batchIndexes.get(entry.target))
      .filter((batchIndex) => batchIndex !== undefined)

    if (!batchIndexes.length) return

    this.requestBatchesThrough(Math.max(...batchIndexes))
  }

  /**
   * Request every batch up to and including the given one, so a batch is
   * never shown before the batches above it.
   */
  requestBatchesThrough (lastBatchIndex) {
    this.batches
      .slice(0, lastBatchIndex + 1)
      .filter((batch) => !batch.isRequested)
      .forEach((batch) => this.requestBatch(batch))
  }

  requestBatch (batch) {
    batch.isRequested = true
    batch.handles = batch.placeholders.map(
      (placeholder) => placeholder.dataset.cardHandle,
    )

    batch.placeholders.forEach((placeholder) =>
      this.cardObserver.unobserve(placeholder),
    )

    const handlesToFetch = [...new Set(batch.handles)].filter(
      (productHandle) => !this.productCardCache.has(productHandle),
    )

    if (handlesToFetch.length) {
      this.fetchProductCards(handlesToFetch)
    }

    const cardsHtml = Promise.all(
      batch.handles.map((productHandle) =>
        this.productCardCache.get(productHandle),
      ),
    )

    /* Render in page order, even if a later batch's request returns first */
    this.renderQueue = this.renderQueue
      .then(() => cardsHtml)
      .then((html) => this.renderBatch(batch, html))
      .catch((error) => this.logError('productCard:renderFailed', error))
  }

  fetchProductCards (productHandles) {
    const url = new URL(
      `${window.Shopify.routes.root}search`,
      window.location.origin,
    )

    url.searchParams.set('q', productHandles.join(' '))
    url.searchParams.set('section_id', 'product-card-batch')

    const request = fetch(url)
      .then((response) => {
        if (!response.ok) {
          throw new Error(`Section request failed: ${response.status}`)
        }

        return response.text()
      })
      .then((sectionHtml) =>
        window.BAO.sectionRenderer.convertToDOM(sectionHtml),
      )
      .catch((error) => {
        productHandles.forEach((productHandle) =>
          this.productCardCache.delete(productHandle),
        )

        this.logError('productCard:fetchFailed', error)
      })

    productHandles.forEach((productHandle) => {
      this.productCardCache.set(
        productHandle,
        request.then(
          (sectionDocument) =>
            sectionDocument?.querySelector(
              `[data-product-card-handle="${productHandle}"] article`,
            )?.outerHTML,
        ),
      )
    })
  }

  /* Insert a whole batch in one frame */
  renderBatch (batch, cardsHtml) {
    return new Promise((resolve) => {
      window.requestAnimationFrame(() => {
        const firstRowSize = batch.index === 0 ? this.getColumnCount() : 0

        batch.placeholders.forEach((placeholder, index) => {
          /* Skip hits that now show a different product */
          if (placeholder.dataset.cardHandle !== batch.handles[index]) return

          this.insertProductCard(placeholder, cardsHtml[index], {
            isFirstRow: index < firstRowSize,
          })
        })

        /* Cards that failed are retried when a batch below them is needed */
        batch.isRequested = batch.placeholders.every(
          (placeholder) =>
            !placeholder.isConnected || this.isCardRendered(placeholder),
        )

        resolve()
      })
    })
  }

  insertProductCard (placeholder, productCardHtml, { isFirstRow = false } = {}) {
    const productHandle = placeholder.dataset.cardHandle

    if (!productCardHtml) return
    if (!placeholder.isConnected) return
    if (this.isCardRendered(placeholder)) return

    placeholder.classList.remove('prd-Card-placeholder')
    placeholder.dataset.productHandle = productHandle
    placeholder.innerHTML = productCardHtml

    this.imageReveals.set(placeholder, { isSettled: false, isRevealed: false })
    this.prepareCardImages(placeholder, isFirstRow)

    /* Emit collection ready event to trigger collection scroll position */
    window.BAO.dispatchEvent(window.BAO.EVENTS.COLLECTION.PRODUCT_RENDERED, {
      productHandle,
    })
  }

  /**
   * Card images are parked until the card's turn in the download queue (see
   * pumpImageQueue). The image on show downloads first. The hidden one (hover,
   * or the main image in outfit mode) waits until no image on show is waiting,
   * unless the card is hovered or swiped. Once loaded, an image appears after
   * the card before it has (see revealSettledImages).
   */
  prepareCardImages (placeholder, isFirstRow) {
    const media = placeholder.querySelector('.prd-Card_MediaContainer')
    const images = media ? this.getCardImages(media) : {}

    if (!images.visible) {
      this.markImageSettled(placeholder)
      return
    }

    media
      .querySelectorAll('[data-product-card-el^="media"] img')
      .forEach((image) => this.deferImage(image))

    media.classList.add('prd-Card_MediaContainer-loading')
    media.dataset.imageReveal = 'pending'

    if (images.hidden) {
      this.holdImageSwap(media)
    }

    const controller = new AbortController()
    const { signal } = controller

    const handleSettled = (event) => {
      const { visible } = this.getCardImages(media)

      /* Ignore the hidden image, unless the visible one has been swapped out */
      if (event.target !== visible && visible?.isConnected) return

      controller.abort()

      const loadDuration = this.getLoadDuration(event.target)

      /* Decode first, so the image paints in the same frame the skeleton goes */
      const decoded =
        event.type === 'load' && event.target.decode
          ? event.target.decode().catch(() => {})
          : Promise.resolve()

      decoded.then(() => this.markImageSettled(placeholder, loadDuration))
    }

    media.addEventListener('load', handleSettled, { capture: true, signal })
    media.addEventListener('error', handleSettled, { capture: true, signal })

    /* Hovering or swiping a card loads both its images straight away */
    const loadImagesNow = () => this.loadDeferredImages(media, 'auto')

    media.addEventListener('pointerenter', loadImagesNow, { once: true })

    media
      .querySelector('[data-card-switcher-images]')
      ?.addEventListener('scroll', loadImagesNow, {
        once: true,
        passive: true,
      })

    this.imageQueue.push({ media, isFirstRow, isStarted: false })
    this.imageObserver.observe(media)
  }

  /**
   * Hovering a card swaps to its other image, so don't swap until both have
   * loaded, otherwise the card goes blank (see product-card.pcss).
   */
  holdImageSwap (media) {
    media.dataset.imageSwap = 'pending'

    const handleImageLoaded = () => {
      const images = media.querySelectorAll(
        '[data-product-card-el^="media"] img',
      )

      const isReady = [...images].every(
        (image) => image.complete && !image.hasAttribute('data-deferred-src'),
      )

      if (!isReady) return

      delete media.dataset.imageSwap
      media.removeEventListener('load', handleImageLoaded, true)
      media.removeEventListener('error', handleImageLoaded, true)
    }

    media.addEventListener('load', handleImageLoaded, true)
    media.addEventListener('error', handleImageLoaded, true)
  }

  /* The image on show depends on the card style: outfit shows the hover image */
  getCardImages (media) {
    const main = media.querySelector('[data-product-card-el="media"] img')
    const hover = media.querySelector(
      '[data-product-card-el="mediaHover"] img',
    )
    const isOutfit = this.dataset.cardStyle === 'outfit' && hover

    return {
      visible: isOutfit ? hover : main,
      hidden: isOutfit ? main : hover,
    }
  }

  handleImageIntersections (entries) {
    entries.forEach(({ target, isIntersecting }) => {
      if (isIntersecting) {
        this.nearbyMedia.add(target)
      } else {
        this.nearbyMedia.delete(target)
      }
    })

    this.pumpImageQueue()
  }

  /**
   * Start the next images in grid order, keeping IMAGE_LOAD_CONCURRENCY
   * downloading at once. Images on show near the viewport go first. Hidden
   * images only start once none of those are waiting or downloading.
   */
  pumpImageQueue () {
    /* Cancel downloads for cards that have left the page */
    this.imagesInFlight.forEach((kind, image) => {
      if (image.isConnected) return

      image.removeAttribute('srcset')
      image.removeAttribute('src')
      this.imagesInFlight.delete(image)
    })

    /* Drop cards that have left the page or have nothing left to load */
    this.imageQueue = this.imageQueue.filter((entry) => {
      const isDone =
        entry.isStarted && !entry.media.querySelector('img[data-deferred-src]')

      if (entry.media.isConnected && !isDone) return true

      this.imageObserver.unobserve(entry.media)
      return false
    })

    const canStart = () => this.imagesInFlight.size < IMAGE_LOAD_CONCURRENCY
    const isNearby = (entry) => this.nearbyMedia.has(entry.media)

    /* On-screen cards go first, then those just off it, each in grid order */
    const waiting = this.imageQueue.filter(
      (entry) => !entry.isStarted && isNearby(entry),
    )

    const onScreen = waiting.filter((entry) => {
      const { top, bottom } = entry.media.getBoundingClientRect()
      return bottom > 0 && top < window.innerHeight
    })

    for (const entry of new Set([...onScreen, ...waiting])) {
      if (!canStart()) return

      entry.isStarted = true

      this.startImage(
        this.getCardImages(entry.media).visible,
        'visible',
        entry.isFirstRow ? 'high' : 'auto',
      )
    }

    const isVisibleImageWaiting = this.imageQueue.some(
      (entry) => !entry.isStarted && isNearby(entry),
    )
    const isVisibleImageLoading = [...this.imagesInFlight.values()].includes(
      'visible',
    )

    if (isVisibleImageWaiting || isVisibleImageLoading) return

    for (const entry of this.imageQueue) {
      if (!canStart()) return
      if (!entry.isStarted || !isNearby(entry)) continue

      entry.media
        .querySelectorAll('img[data-deferred-src]')
        .forEach((image) => {
          if (canStart()) this.startImage(image, 'hidden', 'low')
        })
    }
  }

  startImage (image, kind, fetchPriority) {
    if (!image?.hasAttribute('data-deferred-src')) return

    this.imagesInFlight.set(image, kind)
    this.imageStartTimes.set(image, window.performance.now())

    const handleDone = () => {
      this.imagesInFlight.delete(image)
      this.pumpImageQueue()
    }

    image.addEventListener('load', handleDone, { once: true })
    image.addEventListener('error', handleDone, { once: true })

    image.loading = 'eager'
    this.loadImage(image, fetchPriority)
  }

  getLoadDuration (image) {
    const startTime = this.imageStartTimes.get(image)

    return startTime ? window.performance.now() - startTime : 0
  }

  /* The image on show swaps with the card style, so load it for cards already underway */
  handleCardStyleChange () {
    this.imageQueue
      .filter((entry) => entry.isStarted)
      .forEach((entry) => this.loadDeferredImages(entry.media, 'auto'))
  }

  /* The card's visible image has loaded (or failed, or there isn't one) */
  markImageSettled (placeholder, loadDuration = 0) {
    const reveal = this.imageReveals.get(placeholder)
    if (!reveal || reveal.isSettled) return

    reveal.isSettled = true
    reveal.timer = window.setTimeout(
      () => this.skipImagesBefore(placeholder),
      Math.max(IMAGE_REVEAL_MIN_WAIT, loadDuration),
    )

    this.revealSettledImages()
  }

  /**
   * An image has waited long enough, so stop waiting on earlier cards that
   * still haven't loaded. They appear whenever they're ready, and the rest
   * of the grid carries on in order.
   */
  skipImagesBefore (placeholder) {
    const index = this.placeholders.indexOf(placeholder)
    if (index < 0) return

    this.placeholders.slice(0, index).forEach((previous) => {
      const reveal = this.imageReveals.get(previous)

      if (reveal) {
        reveal.isSkipped = !reveal.isRevealed
      } else {
        this.imageReveals.set(previous, { isSkipped: true })
      }
    })

    this.revealSettledImages()
  }

  /**
   * Walk the grid in order and show each loaded image once every card
   * before it is showing (or has been skipped).
   */
  revealSettledImages () {
    let isPreviousDone = true

    this.placeholders.forEach((placeholder) => {
      const reveal = this.imageReveals.get(placeholder)

      if (reveal?.isSettled && (isPreviousDone || reveal.isSkipped)) {
        this.revealImage(placeholder)
      }

      isPreviousDone = Boolean(reveal?.isRevealed || reveal?.isSkipped)
    })
  }

  revealImage (placeholder) {
    const reveal = this.imageReveals.get(placeholder)
    if (!reveal || reveal.isRevealed) return

    reveal.isRevealed = true
    window.clearTimeout(reveal.timer)

    const media = placeholder.querySelector('.prd-Card_MediaContainer')
    if (!media?.dataset.imageReveal) return

    /* Swap the skeleton for the image in the same frame, with no fade */
    media.dataset.imageReveal = 'shown'
    media.classList.remove('prd-Card_MediaContainer-loading')
  }

  /* Hold back an image's download by parking its sources */
  deferImage (image) {
    image.dataset.deferredSrc = image.getAttribute('src') || ''
    image.dataset.deferredSrcset = image.getAttribute('srcset') || ''

    image.removeAttribute('srcset')
    image.removeAttribute('src')
  }

  loadDeferredImages (container, fetchPriority = 'low') {
    container
      .querySelectorAll('img[data-deferred-src]')
      .forEach((image) => this.loadImage(image, fetchPriority))
  }

  /* Put a parked image's sources back so it downloads */
  loadImage (image, fetchPriority) {
    const { deferredSrc, deferredSrcset } = image.dataset

    delete image.dataset.deferredSrc
    delete image.dataset.deferredSrcset

    image.fetchPriority = fetchPriority

    if (deferredSrcset) image.setAttribute('srcset', deferredSrcset)
    if (deferredSrc) image.setAttribute('src', deferredSrc)
  }

  isCardRendered (placeholder) {
    return placeholder.dataset.productHandle === placeholder.dataset.cardHandle
  }

  /* A hit element reused for a different product goes back to a skeleton */
  resetPlaceholder (placeholder) {
    if (!placeholder.childElementCount) return

    placeholder.replaceChildren()
    placeholder.classList.add('prd-Card-placeholder')
    delete placeholder.dataset.productHandle

    window.clearTimeout(this.imageReveals.get(placeholder)?.timer)
    this.imageReveals.delete(placeholder)
  }

  getColumnCount () {
    const list = this.querySelector('.ais-Hits-list')
    if (!list) return 0

    return window
      .getComputedStyle(list)
      .gridTemplateColumns.split(' ')
      .filter(Boolean).length
  }

  /**
   * Batch holding the product the shopper last clicked through to, so it
   * renders and collection-scroll-position.js can scroll back to it.
   */
  getScrollRestoreBatchIndex () {
    const href = window.BAO.collectionScrollPosition?.lastClickedItemHref
    const productHandle = href?.split('/products/')[1]?.split(/[/?#]/)[0]

    if (!productHandle) return -1

    const placeholder = this.placeholders.find(
      (item) => item.dataset.cardHandle === productHandle,
    )

    return this.batchIndexes.get(placeholder) ?? -1
  }

  logError (label, error) {
    // eslint-disable-next-line no-console
    console.error(`<${this.localName}> ${label}`, error)
  }

  get collectionId () {
    return this.dataset.collectionId || ''
  }
}

if (!customElements.get('algolia-instant-search-widget')) {
  customElements.define(
    'algolia-instant-search-widget',
    AlgoliaInstantSearchWidget,
  )
}
